/**
 * Settings control for desktop app updates: shows current version state and a
 * manual "Check for updates" button, plus "Restart now" once an update has been
 * downloaded in the background (or "Install & Restart" if that download failed).
 * Renders nothing in the browser (desktop/Tauri only).
 */
import { useDesktopUpdater } from '@/lib/desktopUpdater';
import { Button } from '@/components/ui/button';
import { RefreshCw, Download, CheckCircle, AlertCircle } from 'lucide-react';

export function DesktopUpdateSettings() {
  const { status, version, error, progress, check, install, isDesktop } = useDesktopUpdater();
  if (!isDesktop) return null;

  const busy = status === 'checking' || status === 'downloading' || status === 'installing';

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 dark:text-white">Desktop app updates</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
            {status === 'checking' && (<><RefreshCw className="h-4 w-4 animate-spin" /> Checking…</>)}
            {status === 'uptodate' && (<><CheckCircle className="h-4 w-4 text-green-600" /> You're on the latest version.</>)}
            {status === 'available' && (<><Download className="h-4 w-4 text-[#5B4B8A]" /> Version {version} is available.</>)}
            {status === 'ready' && (<><Download className="h-4 w-4 text-[#5B4B8A]" /> Version {version} is downloaded. Restart to finish updating.</>)}
            {status === 'downloading' && (<><RefreshCw className="h-4 w-4 animate-spin" /> Downloading {version}… {progress}%</>)}
            {status === 'installing' && (<><RefreshCw className="h-4 w-4 animate-spin" /> Installing… restarting shortly.</>)}
            {status === 'error' && (<><AlertCircle className="h-4 w-4 text-red-600" /> <span className="break-words">{error}</span></>)}
            {status === 'idle' && 'Check for available updates.'}
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          {status === 'available' || status === 'ready' ? (
            <Button size="sm" onClick={() => void install()}>
              {status === 'ready' ? 'Restart now' : <>Install &amp; Restart</>}
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => void check()} disabled={busy}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Check for updates
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
